import { LocalizeContextProps, withLocalize } from "react-localize-redux";
import * as React from "react";
import { useEffect, useRef } from "react";
import { Label } from "office-ui-fabric-react";
import { AuthenticationResult } from "@azure/msal-browser";
import { OfficeWrapper } from "../../services/OfficeWrapper";
import { TranslationService } from "../../services/TranslationService";
import { Logger } from "../../services/Logger";

export interface SharePointConsentRedirectComponentProps extends LocalizeContextProps {
    translationService: TranslationService;
    officeWrapper: OfficeWrapper;
    logger: Logger;
    result: AuthenticationResult | null;
    error: string | null;
}

// Runs inside the SharePoint consent dialog once MSAL's redirect flow has completed (see
// SharePointConsentRedirectService.completePendingRedirect). Posts the outcome back to
// SharePointConsentDialog.ts via Office.context.ui.messageParent; the dialog itself is then
// closed by the parent (taskpane) side, mirroring LoginDialog.tsx's pattern.
const SharePointConsentRedirectComponent: React.FC<SharePointConsentRedirectComponentProps> = (props) => {
    // This page is its own React root (no AppComponent), so translations must be initialised here, before first paint.
    const translationsInitialised = useRef(false);
    if (!translationsInitialised.current) {
        translationsInitialised.current = true;
        props.translationService.getTranslations(props);
    }

    useEffect(() => {
        if (props.result) {
            props.logger.info(
                "SharePointConsentRedirectComponent: MSAL redirect completed - handleRedirectPromise() " +
                    "resolved a result; posting it back to SharePointConsentDialog via messageParent"
            );
        } else {
            props.logger.error(
                "SharePointConsentRedirectComponent: MSAL redirect completed with an error - " +
                    "posting it back to SharePointConsentDialog via messageParent",
                props.error
            );
        }
        const message = props.result
            ? JSON.stringify(props.result)
            : JSON.stringify({ error: props.error ?? "Unknown SharePoint consent error" });
        props.officeWrapper.messageParent(message);
        // Posts exactly once on mount; the parent closes the dialog on receipt.
    }, []);

    return (
        <>
            {props.error ? <Label>{props.translate("AUTHENTICATION.LOGIN_ERROR")}</Label> : null}
            {/* Debug detail — visible directly in the dialog since it can error before there's time to open
             *  its own DevTools. props.error is already a fully-described string (see describeError). */}
            {props.error ? <Label>{props.error}</Label> : null}
        </>
    );
};

export default withLocalize(SharePointConsentRedirectComponent);
